/**
 * FERP Portal - Spatial Layout Hook
 *
 * DRR-179: Fetches LOCATED_AT scope edges with coordinate metadata for
 * positioning rooms/zones on the SpatialRenderer.
 *
 * Coordinates are normalized (0.0–1.0) relative to the floor plan bounding box.
 * When no coordinates exist, SpatialRenderer falls back to auto-grid (DRR-178).
 *
 * Usage:
 * ```tsx
 * const { layout, hasCoordinates, loading } = useSpatialLayout(floor);
 * ```
 */

import { useMemo } from 'react';
import { gql } from '@apollo/client/core';
import { useQuery } from '@apollo/client/react';

const GET_LOCATED_AT_EDGES = gql`
  query GetLocatedAtEdges($edgeType: ScopeEdgeType) {
    scopeEdges(edgeType: $edgeType) {
      sourceName
      targetName
      edgeType
      metadata
    }
  }
`;

export interface SpatialNode {
  /** Room/zone actor ID */
  actorId: string;
  /** Display name (e.g., "Room 101") */
  name: string;
  /** Normalized X coordinate (0.0–1.0), null if not mapped */
  x: number | null;
  /** Normalized Y coordinate (0.0–1.0), null if not mapped */
  y: number | null;
  /** Floor number */
  floor: number;
  /** Optional zone identifier (e.g., "wing-a") */
  zone?: string;
  /** Optional room type (e.g., "bedroom", "common") */
  roomType?: string;
}

/** DRR-179: Floor plan document reference for signed URL resolution */
export interface FloorPlanRef {
  /** Floor number (1-indexed) */
  floor: number;
  /** Document primitive ID (resolve via useFloorPlanUrl) */
  documentId: string;
  /** Display label (e.g., "First Floor") */
  label?: string;
}

export interface SpatialLayoutReturn {
  /** All spatial nodes with coordinates (or null coords if unmapped) */
  layout: SpatialNode[];
  /** Whether any nodes have coordinates (true = positioned mode, false = auto-grid) */
  hasCoordinates: boolean;
  /** Available floors */
  floors: number[];
  /** DRR-179: Floor plan document refs per floor (for signed URL resolution) */
  floorPlanRefs: FloorPlanRef[];
  /** Loading state */
  loading: boolean;
}

interface ScopeEdgesData {
  scopeEdges?: Array<{
    sourceName: string;
    targetName: string;
    edgeType: string;
    metadata?: Record<string, unknown>;
  }>;
}

export function useSpatialLayout(floor?: number): SpatialLayoutReturn {
  const { data, loading } = useQuery<ScopeEdgesData>(GET_LOCATED_AT_EDGES, {
    variables: { edgeType: 'LOCATED_AT' },
    fetchPolicy: 'cache-first',
  });

  const result = useMemo<Omit<SpatialLayoutReturn, 'loading'>>(() => {
    const edges = data?.scopeEdges ?? [];
    if (edges.length === 0) {
      return { layout: [], hasCoordinates: false, floors: [], floorPlanRefs: [] };
    }

    let hasAnyCoords = false;
    const floorSet = new Set<number>();
    // DRR-179: Collect floor plan document refs from edge metadata
    const floorPlanMap = new Map<number, FloorPlanRef>();

    const nodes: SpatialNode[] = edges.map((edge) => {
      // Edge metadata contains { x, y, floor, zone?, room_type?, floor_plan_doc_id? }
      // sourceName = room actor name, targetName = facility actor name
      const meta = edge.metadata;
      const x = typeof meta?.x === 'number' ? meta.x : null;
      const y = typeof meta?.y === 'number' ? meta.y : null;
      const floorNum = typeof meta?.floor === 'number' ? meta.floor : 1;

      if (x !== null && y !== null) hasAnyCoords = true;
      floorSet.add(floorNum);

      // Extract floor plan document ref if present on any edge for this floor
      const docId = typeof meta?.floor_plan_doc_id === 'string' ? meta.floor_plan_doc_id : null;
      if (docId && !floorPlanMap.has(floorNum)) {
        floorPlanMap.set(floorNum, {
          floor: floorNum,
          documentId: docId,
          label: typeof meta?.floor_label === 'string' ? meta.floor_label : undefined,
        });
      }

      return {
        actorId: edge.sourceName, // Room actor
        name: edge.sourceName,
        x,
        y,
        floor: floorNum,
        zone: typeof meta?.zone === 'string' ? meta.zone : undefined,
        roomType: typeof meta?.room_type === 'string' ? meta.room_type : undefined,
      };
    });

    // Filter by floor if specified
    const filtered = floor != null
      ? nodes.filter((n) => n.floor === floor)
      : nodes;

    return {
      layout: filtered,
      hasCoordinates: hasAnyCoords,
      floors: Array.from(floorSet).sort((a, b) => a - b),
      floorPlanRefs: Array.from(floorPlanMap.values()).sort((a, b) => a.floor - b.floor),
    };
  }, [data, floor]);

  return { ...result, loading };
}
